// COPIED from ptc-core web/src/shell/FeedbackDialog.tsx @ de39fff (fleet feedback contract v1, 2026-10-21).
// Changes from the source: `app: 'supervision'` (contract §3); the 400 field errors come from api/feedback's
// FeedbackValidationError (core's ApiValidationError, which has no client here); Tailwind classes ported to Fluent
// makeStyles (ptc-statetax's port); i18n from src/i18n (supervision's rule). Behaviour is unchanged. Conformance:
// src/components/feedback/FeedbackWidgetConformance.test.tsx.
import { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Trans, useTranslation } from '../../i18n';
import { FeedbackValidationError, useCreateFeedback, type FeedbackKind } from '../../api/feedback';
import { buildFeedbackContext, type FeedbackContext } from '../../feedback/feedbackContext';

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  text: { fontSize: tokens.fontSizeBase300, margin: 0 },
  warning: { fontSize: tokens.fontSizeBase300, margin: 0, color: tokens.colorPaletteMarigoldForeground2 },
  error: { fontSize: tokens.fontSizeBase300, margin: 0, color: tokens.colorPaletteRedForeground1 },
  details: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  dl: {
    marginTop: tokens.spacingVerticalXS,
    marginBottom: 0,
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: tokens.spacingHorizontalS,
  },
  dd: { margin: 0, wordBreak: 'break-all' },
});

/**
 * "Report a problem" / "Suggest an improvement" (feedback-loop spec §3). Two entries, not one form
 * with a dropdown: the kind sets the triage lane, and a dropdown left at its default mislabels
 * half the reports.
 *
 * What is sent is SHOWN before it is sent (§3.2). The SSN/EIN warning sits above the text box so
 * the server-side guard rarely has to fire; when it does, its message is shown on the field.
 */
interface FeedbackDialogProps {
  kind: FeedbackKind | null;
  onClose: () => void;
  /** Test seam; production captures the live page. */
  captureContext?: () => FeedbackContext;
}

const COPY: Record<FeedbackKind, { title: string; label: string }> = {
  problem: { title: 'shell.feedback.problemTitle', label: 'shell.feedback.problemLabel' },
  improvement: { title: 'shell.feedback.improvementTitle', label: 'shell.feedback.improvementLabel' },
};

export function FeedbackDialog({ kind, onClose, captureContext = buildFeedbackContext }: FeedbackDialogProps) {
  if (kind === null) return null;
  // Keyed on kind so switching between the two entries starts a fresh form.
  return <FeedbackForm key={kind} kind={kind} onClose={onClose} captureContext={captureContext} />;
}

function FeedbackForm({ kind, onClose, captureContext }: Required<FeedbackDialogProps> & { kind: FeedbackKind }) {
  const { t } = useTranslation();
  const styles = useStyles();
  // Captured when the dialog OPENS: the route the reporter was on, not wherever they click Send.
  const context = useMemo(() => captureContext(), [captureContext]);
  const [text, setText] = useState('');
  const [expected, setExpected] = useState('');
  const [creditByName, setCreditByName] = useState(true); // DP-FB4: default ON
  const create = useCreateFeedback();

  const fieldErrors = create.error instanceof FeedbackValidationError ? create.error : null;
  const otherError = create.error && !fieldErrors;
  const reference = create.data?.reference;

  function send() {
    create.mutate({
      kind,
      text: text.trim(),
      expected: kind === 'problem' && expected.trim() ? expected.trim() : undefined,
      creditByName,
      app: 'supervision',
      context,
    });
  }

  return (
    <Dialog open modalType="modal" onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t(COPY[kind].title)}</DialogTitle>
          <DialogContent>
            {reference ? (
              <p role="status" className={styles.text}>
                <Trans i18nKey="shell.feedback.thanks" values={{ reference }} components={{ strong: <strong /> }} />
              </p>
            ) : (
              <div className={styles.form}>
                <p className={styles.warning} role="note">{t('shell.feedback.sensitiveWarning')}</p>
                <Field
                  label={t(COPY[kind].label)}
                  required
                  validationMessage={fieldErrors?.firstFor('text')}
                >
                  <Textarea value={text} onChange={(_, d) => setText(d.value)} rows={5} maxLength={4000} />
                </Field>
                {kind === 'problem' && (
                  <Field label={t('shell.feedback.expected')} validationMessage={fieldErrors?.firstFor('expected')}>
                    <Textarea value={expected} onChange={(_, d) => setExpected(d.value)} rows={3} maxLength={2000} />
                  </Field>
                )}
                <Checkbox
                  checked={creditByName}
                  onChange={(_, d) => setCreditByName(d.checked === true)}
                  label={t('shell.feedback.credit')}
                />
                <details className={styles.details}>
                  <summary>{t('shell.feedback.attachSummary')}</summary>
                  <dl className={styles.dl}>
                    <dt>{t('shell.feedback.contextPage')}</dt><dd className={styles.dd}>{context.route}</dd>
                    <dt>{t('shell.feedback.contextVersion')}</dt><dd className={styles.dd}>{context.buildSha}</dd>
                    <dt>{t('shell.feedback.contextScreen')}</dt><dd className={styles.dd}>{context.viewport}</dd>
                    <dt>{t('shell.feedback.contextTime')}</dt><dd className={styles.dd}>{context.clientTime} ({context.tz})</dd>
                    <dt>{t('shell.feedback.contextLanguage')}</dt><dd className={styles.dd}>{context.uiLanguage}</dd>
                    <dt>{t('shell.feedback.contextBrowser')}</dt><dd className={styles.dd}>{context.userAgent}</dd>
                    <dt>{t('shell.feedback.contextErrors')}</dt><dd className={styles.dd}>{context.recentErrors.length}</dd>
                  </dl>
                </details>
                {otherError && (
                  <p role="alert" className={styles.error}>
                    {t('shell.feedback.sendFailed')}
                  </p>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {reference ? (
              <Button appearance="primary" onClick={onClose}>{t('common.close')}</Button>
            ) : (
              <>
                <Button appearance="secondary" onClick={onClose}>{t('common.cancel')}</Button>
                <Button appearance="primary" disabled={!text.trim() || create.isPending} onClick={send}>
                  {create.isPending ? t('shell.feedback.sending') : t('shell.feedback.send')}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
